<template>
  <div class="bware-schrott-panel">
    <!-- Übersicht -->
    <v-card v-if="!selectedListe" class="status-overview-card mb-6">
      <v-card-title class="d-flex align-center">
        <v-icon color="error" size="24" class="mr-2">mdi-delete-variant</v-icon>
        Schrott B-Ware
        <v-spacer />
        <v-chip color="error" size="small" variant="tonal">
          {{ gefilterteListen.length }} Listen
        </v-chip>
      </v-card-title>

      <v-divider class="card-divider" />

      <v-card-text class="py-4">
        <v-text-field
          v-model="suche"
          label="Listen suchen"
          variant="outlined"
          bg-color="rgba(30, 30, 40, 0.5)"
          hide-details="auto"
          clearable
          class="form-field mb-4"
          color="error"
          hint="Suche nach B-Ware-ID, Ersteller oder Bearbeiter"
        >
          <template #prepend-inner>
            <v-icon color="error" size="small">mdi-magnify</v-icon>
          </template>
        </v-text-field>

        <v-data-table
          :headers="listenHeaders"
          :items="gefilterteListen"
          :loading="loading.listen"
          item-value="id"
          class="elevation-0"
          no-data-text="Keine abgeschlossenen Schrott-Listen vorhanden"
        >
          <template #[`item.bware_id`]="{ item }">
            <strong class="text-error">{{ item.bware_id || '—' }}</strong>
          </template>
          <template #[`item.status`]="{ item }">
            <v-chip size="small" color="success" variant="tonal">
              Abgeschlossen
            </v-chip>
          </template>
          <template #[`item.abgeschlossen_am`]="{ item }">
            {{
              item.abgeschlossen_am
                ? new Date(item.abgeschlossen_am).toLocaleString('de-DE')
                : '—'
            }}
          </template>
          <template #[`item.summe_schrott`]="{ item }">
            <span class="text-error font-weight-medium">{{ item.summe_schrott }}</span>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn
              icon="mdi-pencil"
              size="small"
              variant="text"
              color="indigo"
              title="Liste bearbeiten"
              @click="loadListe(item.id)"
            />
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Detail: SAP Bearbeitung -->
    <div v-else>
      <v-btn class="mb-4" prepend-icon="mdi-arrow-left" @click="backToList">
        Zurück zur Übersicht
      </v-btn>

      <v-row>
        <v-col cols="12" md="8" lg="6">
          <v-card class="sap-card mb-4">
            <v-card-title class="bg-indigo-darken-1">
              <v-icon class="mr-2">mdi-clipboard-list</v-icon>
              SAP Bearbeitung
              <v-spacer />
              <v-chip size="small" color="white" variant="outlined">
                {{ selectedListe.bware_id || `Liste #${selectedListe.id}` }}
              </v-chip>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <div class="d-flex ga-4 mb-4 flex-wrap text-caption">
                <div>
                  <div class="text-medium-emphasis">Bearbeiter</div>
                  <div>{{ selectedListe.user_name || '—' }}</div>
                </div>
                <div>
                  <div class="text-medium-emphasis">Abgeschlossen</div>
                  <div>
                    {{
                      selectedListe.abgeschlossen_am
                        ? new Date(selectedListe.abgeschlossen_am).toLocaleString('de-DE')
                        : '—'
                    }}
                  </div>
                </div>
                <div>
                  <div class="text-medium-emphasis">Schrott gesamt</div>
                  <div class="text-error font-weight-bold">{{ schrottGesamt }}</div>
                </div>
              </div>

              <v-btn
                block
                color="primary"
                variant="tonal"
                class="mb-2"
                prepend-icon="mdi-content-copy"
                @click="copyCmmfToClipboard"
              >
                Alle CMMF in Zwischenablage kopieren
              </v-btn>
              <v-btn
                block
                color="indigo"
                variant="tonal"
                class="mb-4"
                prepend-icon="mdi-numeric"
                @click="copyMengenToClipboard"
              >
                Alle Mengen in Zwischenablage kopieren
              </v-btn>

              <v-table density="comfortable" class="sap-table">
                <thead>
                  <tr>
                    <th>CMMF</th>
                    <th>Artikel</th>
                    <th class="text-center">Schrott</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="pos in schrottPositionen" :key="pos.id">
                    <td>
                      <code>{{ pos.cmmf_nummer || '—' }}</code>
                    </td>
                    <td>
                      <div class="text-body-2">{{ pos.bezeichnung || '—' }}</div>
                      <div class="text-caption text-grey">
                        EAN: {{ pos.ean || '—' }}
                        <span v-if="pos.commercial_ref"> | SKU: {{ pos.commercial_ref }}</span>
                      </div>
                    </td>
                    <td class="text-center">
                      <v-chip size="small" color="error">{{ pos.schrott_menge }}x</v-chip>
                    </td>
                  </tr>
                  <tr v-if="schrottPositionen.length === 0">
                    <td colspan="3" class="text-center text-medium-emphasis py-4">
                      Keine Schrott-Positionen in dieser Liste
                    </td>
                  </tr>
                </tbody>
              </v-table>

              <v-alert type="info" variant="tonal" density="compact" class="mt-4">
                <strong>{{ schrottPositionen.length }} Artikel</strong>
                ·
                <strong>{{ schrottGesamt }} Stück</strong>
                Schrott
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script setup>
/**
 * Office-Panel: abgeschlossene B-Ware-Listen mit Schrott (SAP/CMMF).
 */
import { ref, computed, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

const toast = useToast();
const client = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

const listen = ref([]);
const selectedListe = ref(null);
const positionen = ref([]);
const suche = ref('');
const loading = ref({ listen: false, detail: false });

const listenHeaders = [
  { title: 'B-Ware-ID', key: 'bware_id', sortable: true },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Bearbeiter', key: 'user_name', sortable: true },
  { title: 'Abgeschlossen am', key: 'abgeschlossen_am', sortable: true },
  { title: 'Artikel', key: 'anzahl_positionen', sortable: true, align: 'center' },
  { title: 'Schrott', key: 'summe_schrott', sortable: true, align: 'center' },
  { title: 'Aktionen', key: 'actions', sortable: false, align: 'end' },
];

const gefilterteListen = computed(() => {
  if (!suche.value) return listen.value;
  const q = suche.value.toLowerCase();
  return listen.value.filter(
    (l) =>
      l.bware_id?.toLowerCase().includes(q) ||
      l.user_name?.toLowerCase().includes(q) ||
      l.erstellt_von_name?.toLowerCase().includes(q)
  );
});

const schrottPositionen = computed(() =>
  positionen.value.filter((p) => Number(p.schrott_menge) > 0)
);

const schrottGesamt = computed(() =>
  schrottPositionen.value.reduce((sum, p) => sum + (Number(p.schrott_menge) || 0), 0)
);

/**
 * Lädt abgeschlossene Listen, die Schrott enthalten.
 */
async function fetchListen() {
  loading.value.listen = true;
  try {
    const response = await client.get('bware/index.php', {
      params: { action: 'get_archiv', nur_schrott: '1' },
    });
    if (response.data?.status === 'success') {
      listen.value = response.data.listen || [];
    } else {
      toast.error(response.data?.message || 'Fehler beim Laden der Listen');
    }
  } catch (error) {
    console.error(error);
    toast.error('Netzwerkfehler beim Laden der Schrott-Listen');
  } finally {
    loading.value.listen = false;
  }
}

/**
 * Öffnet die Detailansicht einer Liste (nur Schrott-Positionen für SAP).
 * @param {number} listeId
 */
async function loadListe(listeId) {
  loading.value.detail = true;
  try {
    const response = await client.get('bware/index.php', {
      params: { action: 'get_liste_positionen', liste_id: listeId },
    });
    if (response.data?.status === 'success') {
      selectedListe.value = response.data.liste;
      positionen.value = response.data.positionen || [];
    } else {
      toast.error(response.data?.message || 'Liste konnte nicht geladen werden');
    }
  } catch (error) {
    console.error(error);
    toast.error('Netzwerkfehler beim Laden der Liste');
  } finally {
    loading.value.detail = false;
  }
}

function backToList() {
  selectedListe.value = null;
  positionen.value = [];
  fetchListen();
}

async function copyCmmfToClipboard() {
  try {
    const cmmfList = schrottPositionen.value
      .filter((pos) => pos.cmmf_nummer)
      .map((pos) => pos.cmmf_nummer)
      .join('\n');
    if (!cmmfList) {
      toast.warning('Keine CMMF-Codes verfügbar');
      return;
    }
    await navigator.clipboard.writeText(cmmfList);
    const anzahl = schrottPositionen.value.filter((pos) => pos.cmmf_nummer).length;
    toast.success(`${anzahl} CMMF-Code${anzahl !== 1 ? 's' : ''} in Zwischenablage kopiert`);
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Kopieren in Zwischenablage');
  }
}

async function copyMengenToClipboard() {
  try {
    const mengenList = schrottPositionen.value
      .filter((pos) => pos.cmmf_nummer)
      .map((pos) => pos.schrott_menge)
      .join('\n');
    if (!mengenList) {
      toast.warning('Keine Mengen verfügbar');
      return;
    }
    await navigator.clipboard.writeText(mengenList);
    const anzahl = schrottPositionen.value.filter((pos) => pos.cmmf_nummer).length;
    toast.success(`${anzahl} Mengen in Zwischenablage kopiert`);
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Kopieren in Zwischenablage');
  }
}

onMounted(() => {
  fetchListen();
});
</script>

<style scoped>
.status-overview-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-divider {
  opacity: 0.1;
}

.form-field {
  margin-bottom: 12px;
}

.sap-card {
  background: rgba(30, 30, 40, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.sap-table {
  background: rgba(50, 50, 60, 0.3);
  border-radius: 4px;
}
</style>
